import React, { useEffect, useState } from "react";
import { LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { fmtDateTime } from "../../lib/time";
import { Label, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { RecoveryDraft } from "../../components/ai";

const STATUSES = ["OPEN", "ACKNOWLEDGED", "INVESTIGATING", "ACTION_REQUIRED", "RESOLVED", "CLOSED"];
const COLORS = {
  OPEN: "#EF4444", ACKNOWLEDGED: "#F59E0B", INVESTIGATING: "#6366F1",
  ACTION_REQUIRED: "#F97316", RESOLVED: "#10B981", CLOSED: "#64748B",
};

export default function Recovery() {
  const [cases, setCases] = useState([]);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("");
  const [outcome, setOutcome] = useState("");

  const load = () => api.get("/recovery").then((r) => setCases(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCase = (c) => { setActive(c); setStatus(c.status); setNote(""); setOutcome(c.outcome || ""); };

  const save = async () => {
    try {
      await api.patch(`/recovery/${active.id}`, { status, note: note || undefined, outcome: outcome || undefined });
      toast.success("Case updated"); setActive(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const grouped = STATUSES.map((s) => ({ s, items: cases.filter((c) => c.status === s) }));

  return (
    <div className="space-y-6">
      <div><Label>Action</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Service Recovery</h1></div>
      {cases.length === 0 ? <EmptyState icon={LifeBuoy} title="No recovery cases" hint="Cases are created automatically from negative feedback." /> : (
        <div className="grid grid-cols-1 gap-4 overflow-x-auto sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 sv-scroll">
          {grouped.map(({ s, items }) => (
            <div key={s} className="min-w-[220px] rounded-xl bg-slate-100/60 p-3" data-testid={`column-${s}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide" style={{ color: COLORS[s] }}>
                  <span className="h-2 w-2 rounded-full" style={{ background: COLORS[s] }} />{s.replace("_", " ")}
                </span>
                <span className="text-xs text-slate-400">{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map((c) => (
                  <button key={c.id} onClick={() => openCase(c)} data-testid={`case-${c.id}`}
                    className="w-full rounded-lg border border-slate-200 bg-white p-3 text-left transition-transform hover:-translate-y-0.5">
                    <p className="text-sm font-semibold text-slate-800">{c.employee_name}</p>
                    <p className="text-xs text-slate-500">{c.problem_category || "Unspecified"}</p>
                    {c.trigger_detail?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid={`case-triggers-${c.id}`}>
                        {c.trigger_detail.map((t) => (
                          <span key={t.key} className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                            Opened by: {t.label}{t.rating != null ? ` ${t.rating}★` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                    {c.comment && <p className="mt-1 line-clamp-2 text-xs text-slate-400">{c.comment}</p>}
                    <p className="mt-1 text-[10px] uppercase text-slate-300">{fmtDateTime(c.opened_at, c.location_id)}</p>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sv-scroll">
          <DialogHeader><DialogTitle>Recovery Case · {active?.employee_name}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-4 text-sm">
              <div className="rounded-lg bg-slate-50 p-3">
                <p><span className="font-semibold">Category:</span> {active.problem_category || "—"}</p>
                <p><span className="font-semibold">Resolved on-site:</span> {active.resolved_on_site ? "Yes" : "No"}</p>
                {active.incident_time && <p><span className="font-semibold">When:</span> {active.incident_time}</p>}
                {active.comment && <p className="mt-1 text-slate-600">"{active.comment}"</p>}
                {(active.contact_name || active.contact_phone || active.contact_email || active.contact_detail) && (
                  <div className="mt-2 border-t border-slate-200 pt-2">
                    <p className="font-semibold">Customer contact</p>
                    {active.contact_name && <p>{active.contact_name}</p>}
                    {active.contact_phone && <p><a className="text-indigo-600 hover:underline" href={`tel:${active.contact_phone}`}>{active.contact_phone}</a></p>}
                    {active.contact_email && <p><a className="text-indigo-600 hover:underline" href={`mailto:${active.contact_email}`}>{active.contact_email}</a></p>}
                    {!active.contact_phone && !active.contact_email && active.contact_detail && <p>{active.contact_detail}</p>}
                    {active.allow_follow_up && <p className="mt-1 text-xs font-semibold text-emerald-600">✓ Customer consented to be contacted</p>}
                  </div>
                )}
              </div>
              {active.actions?.length > 0 && (
                <div>
                  <Label>Activity</Label>
                  <div className="mt-2 space-y-1">
                    {active.actions.map((a, i) => <p key={i} className="text-xs text-slate-500">{fmtDateTime(a.at, active.location_id)} · <b>{a.by}</b>: {a.note}</p>)}
                  </div>
                </div>
              )}
              <RecoveryDraft key={active.id} caseId={active.id} />
              <div>
                <Label className="mb-1 block normal-case tracking-normal text-slate-600">Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="case-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {(status === "RESOLVED" || status === "CLOSED") && (
                <Textarea placeholder="Resolution outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="case-outcome" />
              )}
              <Textarea placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="case-note" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActive(null)}>Cancel</Button>
            <Button onClick={save} data-testid="save-case" className="bg-[#4338CA] hover:bg-[#3730A3]">Update case</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
